import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { QUERY_KEYS } from '../../../../../api/queryKeys'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import Markup from '../../../../atoms/Markup'
import { labelStyle, valueStyle as baseValueStyle } from '../shared/redtailUIHelpers'
import { LinkedContactDetail } from '../shared/LinkedContactDisplay'
import CompleteActivityButton from '../shared/CompleteActivityButton'
import useCategoryOptions from './useCategoryOptions'

const valueStyle = { ...baseValueStyle, marginBottom: '12px' }

const LIST_INVALIDATE_KEYS = [[QUERY_KEYS.redtailDomainQuery, 'list-preset']]

function TaskDetailModal ({ open, onClose, activity }) {
  const { clientId } = useAppContext()
  const categoryOptions = useCategoryOptions(clientId, null, open)

  const categoryName = useMemo(() => {
    if (!activity?.category_id) return null
    const cat = categoryOptions.find(c => c.value === activity.category_id)
    return cat?.label ?? null
  }, [activity?.category_id, categoryOptions])

  if (!activity) return null

  const startFormatted = activity.start_date
    ? dayjs(activity.start_date).format('ddd, MMM D, YYYY')
    : null
  const endFormatted = activity.end_date
    ? dayjs(activity.end_date).format('ddd, MMM D, YYYY')
    : null
  const isCompleted = !!activity.completed

  return (
    <SydModal
      open={open}
      onClose={onClose}
      title={activity.subject || 'Task Details'}
      size='xmedium'
      disablePortal={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <LinkedContactDetail
              contacts={activity.linked_contacts}
              labelStyle={labelStyle}
              valueStyle={valueStyle}
            />
          </div>
          <div style={{ display: 'flex', gap: '24px', flexShrink: 0 }}>
            {startFormatted ? (
              <div>
                <div style={labelStyle}>Start Date</div>
                <div style={valueStyle}>{startFormatted}</div>
              </div>
            ) : null}
            {endFormatted ? (
              <div>
                <div style={labelStyle}>Due Date</div>
                <div style={valueStyle}>{endFormatted}</div>
              </div>
            ) : null}
          </div>
        </div>

        {activity.priority ? (
          <>
            <div style={labelStyle}>Priority</div>
            <div style={valueStyle}>{activity.priority}</div>
          </>
        ) : null}

        {activity.location ? (
          <>
            <div style={labelStyle}>Location</div>
            <div style={valueStyle}>{activity.location}</div>
          </>
        ) : null}

        {categoryName ? (
          <>
            <div style={labelStyle}>Category</div>
            <div style={valueStyle}>{categoryName}</div>
          </>
        ) : null}

        <div style={labelStyle}>Description</div>
        {activity.description ? (
          <div style={valueStyle}>
            <Markup value={activity.description} type='html' style={{ fontSize: 'inherit' }} />
          </div>
        ) : (
          <div style={{ ...valueStyle, color: 'var(--text-color-subtle)', fontStyle: 'italic' }}>
            No description
          </div>
        )}

        {activity.attendees?.length ? (
          <>
            <div style={labelStyle}>Assigned To</div>
            <div style={valueStyle}>
              {activity.attendees.map((a, i) => {
                const name = a.full_name || a.name || `User ${a.user_id ?? a.id ?? i + 1}`
                return (
                  <div key={a.id ?? a.user_id ?? i} style={{ fontSize: 'var(--font-size-sm)' }}>
                    {name}
                  </div>
                )
              })}
            </div>
          </>
        ) : null}

        {isCompleted ? (
          <div style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-growth-500)',
            fontWeight: 600,
            marginBottom: '8px'
          }}
          >
            This task has been completed
          </div>
        ) : null}
      </div>

      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onClose}>
          Close
        </SydButton>
        {!isCompleted ? (
          <CompleteActivityButton
            activityId={activity.id}
            onCompleted={onClose}
            invalidateQueryKeys={LIST_INVALIDATE_KEYS}
            variant='primary'
            size='sm'
          >
            Complete Task
          </CompleteActivityButton>
        ) : null}
      </SydModalActions>
    </SydModal>
  )
}

TaskDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activity: PropTypes.object
}

export default TaskDetailModal
